/**
 * 版（スマホ版／PC版）と文字の大きさ（#115、docs/44 §1・§2・§8、docs/10 §9.14）。
 * main.tsx の最初に読み込み、描く前に <html data-view="phone|pc" data-text="l|m|s"> を付ける
 * （CSP の script-src 'self' があるのでインラインの script にはしない＝まとめて読み込む module の頭で動く）。
 *
 * 版：
 *   - 自動：画面の短い辺（screen の幅と高さの小さいほう）が 767px 以下で、主に指で操作する端末（pointer: coarse）ならスマホ版。
 *     横向きにしてもスマホ版のまま。機種名（UA）では判定しない（iPad の Safari は「Mac」と名乗る）。
 *     指の条件を足したのは、1080p を 150% で使う Windows のノート（screen 1280×720）や Playwright の既定の窓をスマホ版にしないため（docs/44 §8）
 *   - 手動：Cookie gb_view（pc／phone。無ければ自動。1年・端末ごと）。切り替えたら Cookie を書いて読み直す
 *   - スマホの端末で gb_view=pc なら viewport を width=1280 に（PC の CSS とメディアクエリがそのまま効く）
 *   - タブレットで gb_view=phone なら data-view=phone（ログイン前の画面の本体は最大 480px で中央）
 *   - 「スマホ版で表示」を出すのは gb_view=pc の端末と、短い辺が 1099px 以下で指で操作できる端末だけ（普通のパソコンでは出さない）
 * 文字の大きさ：
 *   - 決まる順は 人の設定（Me.preferences）→ 端末の Cookie gb_text（l／m／s）→ 既定（PC版＝大、スマホ版＝中）
 *   - CSS は styles.css の --fs-N を html[data-text] で上書きするだけ（高さ・余白・押すところは変えない）
 * React に依らない（購読は useSyncExternalStore 用の subscribe）
 */
import type { TextSize } from "@genbaryoku/contract";

export type View = "phone" | "pc";

const VIEW_COOKIE = "gb_view";
const TEXT_COOKIE = "gb_text";
const YEAR = 365 * 24 * 60 * 60;
/** スマホ版の上限（短い辺）。docs/44 §1 の2 */
export const PHONE_MAX = 767;
/** 「スマホ版で表示」を出すタブレットの上限（短い辺） */
export const TABLET_MAX = 1099;
/** スマホの端末で PC版を選んだときの幅 */
export const PC_VIEWPORT = "width=1280";

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(";")) {
    const [k, ...v] = part.trim().split("=");
    if (k === name) return decodeURIComponent(v.join("="));
  }
  return null;
}
function writeCookie(name: string, value: string): void {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${YEAR}; Path=/; SameSite=Lax${secure}`;
}
const mq = (q: string) => (typeof window.matchMedia === "function" ? window.matchMedia(q).matches : false);

/** 画面（端末）の短い辺。窓の大きさではない＝横向き・PC版の viewport でも変わらない */
export function shortSide(): number {
  return Math.min(window.screen.width, window.screen.height);
}
/** 自動の判定（Cookie を見ない） */
export function autoView(): View {
  return shortSide() <= PHONE_MAX && mq("(pointer: coarse)") ? "phone" : "pc";
}
/** 手動で選んだ版（無ければ null＝自動） */
export function chosenView(): View | null {
  const v = readCookie(VIEW_COOKIE);
  return v === "pc" || v === "phone" ? v : null;
}
/** いまの版（boot で <html> に付けたもの） */
export function currentView(): View {
  return document.documentElement.dataset.view === "phone" ? "phone" : "pc";
}
/** PC版のときに「スマホ版で表示」を出すか */
export function canOfferPhone(): boolean {
  return chosenView() === "pc" || (shortSide() <= TABLET_MAX && mq("(any-pointer: coarse)"));
}
/**
 * 版を切り替える：Cookie を書いて読み直す。reload() でなく同じ URL を開き直す
 * （reload はスマホのブラウザが前の拡大率を引き継ぎ、PC版が左上だけ拡大されたままになる。開き直すと幅 1280 の全体が画面に収まる）
 */
export function switchView(v: View): void {
  writeCookie(VIEW_COOKIE, v);
  window.location.replace(window.location.href);
}

const SIZES: readonly TextSize[] = ["l", "m", "s"];
const isSize = (v: unknown): v is TextSize => typeof v === "string" && (SIZES as readonly string[]).includes(v);
/** 版の既定（PC版＝大、スマホ版＝中） */
export function defaultTextSize(view: View = currentView()): TextSize {
  return view === "phone" ? "m" : "l";
}
/** いまの文字の大きさ */
export function currentTextSize(): TextSize {
  const v = document.documentElement.dataset.text;
  return isSize(v) ? v : defaultTextSize();
}

const listeners = new Set<() => void>();
/** useSyncExternalStore 用 */
export function subscribeTextSize(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
/** 文字の大きさを当てて、端末の Cookie にも書く（同じなら何もしない） */
export function applyTextSize(size: TextSize): void {
  if (document.documentElement.dataset.text !== size) {
    document.documentElement.dataset.text = size;
    listeners.forEach((fn) => fn());
  }
  if (readCookie(TEXT_COOKIE) !== size) writeCookie(TEXT_COOKIE, size);
}

/** 起動の最初に1回：<html> に data-view・data-text、スマホの端末で PC版なら viewport を広げる */
function boot(): void {
  const auto = autoView();
  const view = chosenView() ?? auto;
  const html = document.documentElement;
  html.dataset.view = view;
  const t = readCookie(TEXT_COOKIE);
  html.dataset.text = isSize(t) ? t : defaultTextSize(view);
  if (auto === "phone" && view === "pc") document.querySelector('meta[name="viewport"]')?.setAttribute("content", PC_VIEWPORT);
}
boot();
